import React, { useEffect, useState } from "react";
import {
  subscribeToRateCard,
  updateRateCard,
} from "../../services/rateCard.service";
import { RateCardConfig } from "../../types/index";
import { Button } from "../../components/common/Button";
import { Input } from "../../components/common/Input";
import { Card } from "../../components/common/Card";
import { toast } from "react-hot-toast";
import { Settings, CreditCard, Truck, Percent, TrendingUp, Building, Map } from "lucide-react";

export const RateCardSection: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<RateCardConfig | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeToRateCard((data: RateCardConfig) => {
      setConfig({
        ...data,
        // Set defaults if new fields are missing in DB to prevent crashes
        communityItemThreshold: data.communityItemThreshold ?? 3,
        communitySmallOrderFee: data.communitySmallOrderFee ?? 10,
        communityLargeOrderFee: data.communityLargeOrderFee ?? 20,
        externalDeliveryBaseFee: data.externalDeliveryBaseFee ?? 40,
        maxDeliveryDistanceKm: data.maxDeliveryDistanceKm ?? 15,
      });
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleChange = (field: keyof RateCardConfig, value: any) => {
    if (!config) return;
    setConfig({ ...config, [field]: value });
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      // Keep legacy fields in sync with the External Base Fee shown in the UI
      const synced: RateCardConfig = {
        ...config,
        deliveryFeeBelowThreshold:
          config.externalDeliveryBaseFee ??
          config.deliveryFeeBase ??
          config.deliveryFeeBelowThreshold ??
          40,
        deliveryFeeBase:
          config.externalDeliveryBaseFee ??
          config.deliveryFeeBase ??
          config.deliveryFeeBelowThreshold ??
          40,
        deliveryFeeAboveThreshold: config.deliveryFeeAboveThreshold ?? 0,
      };
      await updateRateCard(synced);
      toast.success("✅ Settings updated successfully!");
    } catch (error) {
      console.error("Error saving rate card:", error);
      toast.error("❌ Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
        <span className="ml-3 text-gray-600">Loading configuration...</span>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 space-y-6 sm:space-y-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h2 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
          <Settings className="w-6 h-6 text-gray-600" />
          Platform Configuration
        </h2>
        <Button onClick={handleSave} isLoading={saving} className="px-6">
          {saving ? "💾 Saving..." : "✅ Save Changes"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* --- 1. DELIVERY LOGIC (UPDATED) --- */}
        <Card className="p-5 border-l-4 border-l-blue-500 lg:col-span-2">
          <div className="flex items-center gap-2 mb-6">
            <Truck className="text-blue-600" />
            <h3 className="font-bold text-gray-800 text-lg">Delivery Fee Logic</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Community Settings */}
            <div className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2 mb-3 text-blue-800 font-bold">
                    <Building size={18}/> Community (Internal)
                </div>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase">Item Threshold</label>
                        <Input 
                            type="number" 
                            value={String(config.communityItemThreshold)} 
                            onChange={e => handleChange("communityItemThreshold", Number(e.target.value))}
                            placeholder="3"
                        />
                        <p className="text-[10px] text-gray-500 mt-1">Split between small/large order.</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase">Small Fee (₹)</label>
                            <Input 
                                type="number" 
                                value={String(config.communitySmallOrderFee)} 
                                onChange={e => handleChange("communitySmallOrderFee", Number(e.target.value))}
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase">Large Fee (₹)</label>
                            <Input 
                                type="number" 
                                value={String(config.communityLargeOrderFee)} 
                                onChange={e => handleChange("communityLargeOrderFee", Number(e.target.value))}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* External Settings */}
            <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
                <div className="flex items-center gap-2 mb-3 text-orange-800 font-bold">
                    <Map size={18}/> External Delivery
                </div>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase">Base Fee (₹)</label>
                        <Input 
                            type="number" 
                            value={String(config.externalDeliveryBaseFee)} 
                            onChange={e => handleChange("externalDeliveryBaseFee", Number(e.target.value))}
                        />
                        <p className="text-[10px] text-gray-500 mt-1">Standard fee for outside delivery.</p>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase">Max Radius (km)</label>
                        <Input 
                            type="number" step="0.5"
                            value={String(config.maxDeliveryDistanceKm)} 
                            onChange={e => handleChange("maxDeliveryDistanceKm", Number(e.target.value))}
                        />
                    </div>
                </div>
            </div>

            {/* General Rules */}
            <div className="bg-green-50 p-4 rounded-xl border border-green-100">
                <div className="flex items-center gap-2 mb-3 text-green-800 font-bold">
                    <Percent size={18}/> General Rules
                </div>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase">Free Delivery Above (₹)</label>
                        <Input 
                            type="number" 
                            value={String(config.deliveryThreshold)} 
                            onChange={e => handleChange("deliveryThreshold", Number(e.target.value))}
                        />
                        <p className="text-[10px] text-gray-500 mt-1">Overrides all other fees.</p>
                    </div>
                </div>
            </div>
          </div>
        </Card>

        {/* --- 2. PLATFORM FEE SLABS --- */}
        <Card className="p-5 border-l-4 border-l-purple-500">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="text-purple-600" />
            <h3 className="font-bold text-gray-800">Platform Fee Slabs</h3>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Low Fee (₹)</label>
                <Input type="number" value={String(config.platformFeeLow)} onChange={(e) => handleChange("platformFeeLow", Number(e.target.value))} />
                <p className="text-xs text-gray-500 mt-1">Below ₹{config.platformFeeThresholdLow}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mid Fee (₹)</label>
                <Input type="number" value={String(config.platformFeeMid)} onChange={(e) => handleChange("platformFeeMid", Number(e.target.value))} />
                <p className="text-xs text-gray-500 mt-1">₹{config.platformFeeThresholdLow} – ₹{config.platformFeeThresholdHigh}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">High Fee (₹)</label>
                <Input type="number" value={String(config.platformFeeHigh)} onChange={(e) => handleChange("platformFeeHigh", Number(e.target.value))} />
                <p className="text-xs text-gray-500 mt-1">Above ₹{config.platformFeeThresholdHigh}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Share (%)</label>
                <Input type="number" step="1" value={String(((config.customerPlatformFeeShare || 0) * 100).toFixed(0))} onChange={(e) => handleChange("customerPlatformFeeShare", Number(e.target.value) / 100)} />
                <p className="text-xs text-gray-500 mt-1">Paid by customer</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
               <div>
                 <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Low Threshold (₹)</label>
                 <Input type="number" value={String(config.platformFeeThresholdLow)} onChange={e => handleChange("platformFeeThresholdLow", Number(e.target.value))} />
               </div>
               <div>
                 <label className="block text-xs font-bold text-gray-500 uppercase mb-1">High Threshold (₹)</label>
                 <Input type="number" value={String(config.platformFeeThresholdHigh)} onChange={e => handleChange("platformFeeThresholdHigh", Number(e.target.value))} />
               </div>
            </div>
          </div>
        </Card>

        {/* --- 3. PAYMENT & TAXES --- */}
        <Card className="p-5 border-l-4 border-l-orange-500">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="text-orange-600" />
            <h3 className="font-bold text-gray-800">Taxes & Payment</h3>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GST (%)</label>
                    <Input type="number" value={String(config.gstPercentage)} onChange={(e) => handleChange("gstPercentage", Number(e.target.value))} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Chef Commission (%)</label>
                    <Input type="number" value={String(config.chefCommissionPercentage)} onChange={(e) => handleChange("chefCommissionPercentage", Number(e.target.value))} />
                </div>
            </div>
            <Input label="Merchant UPI ID" value={config.upiId || ""} onChange={(e) => handleChange("upiId", e.target.value)} placeholder="e.g. ruchira@okaxis" />
            <div className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
              <input type="checkbox" id="upiEnabled" checked={config.upiEnabled ?? false} onChange={(e) => handleChange("upiEnabled", e.target.checked)} className="w-5 h-5 text-orange-600 rounded" />
              <label htmlFor="upiEnabled" className="font-medium text-gray-700 cursor-pointer">Enable UPI Payment</label>
            </div>
          </div>
        </Card>

      </div>
    </div>
  );
};